import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import config from 'config';
import useCurrentUser from 'hooks/useCurrentUser';
import jwtDecode from 'jwt-decode';
import { useEffect, useRef, useState } from 'react';
import { useGetClientsQuery } from 'services/api/clients.api';
import type { Client } from 'services/types/client';

const BRANDS = [
  'TOYOTA',
  'PEUGEOT',
  'CITROEN',
  'OPEL',
  'HYUNDAI',
  'CHERY',
  'MG',
  'OMODA',
  'JAECOO',
  'NISSAN',
  'BYD',
  'USADOS',
];
const BRANCH_OFFICES = ['Maipú', 'Paicaví', 'Pras Condes'];
const BRUNO_FRITSCH_BUSINESS_UNIT_ID = 22;

/**
 * Bruno Fritsch Demo Page
 * Simulates Autosystem interface to demonstrate the iframe integration
 */
const BrunoFritschDemo = () => {
  const currentUser = useCurrentUser();
  // Fetch real clients from Bruno Fritsch business unit
  const { data: clientsData, isLoading: isLoadingClients } = useGetClientsQuery({
    paginationModel: { page: 0, pageSize: 10 },
    filterModel: { items: [{ field: 'phone', operator: 'contains', value: '9' }] },
    sortModel: [{ field: 'lastMessageDate', sort: 'desc' }],
    searchTerm: '',
    businessUnitId: currentUser?.isAdmin ? BRUNO_FRITSCH_BUSINESS_UNIT_ID : undefined,
  });

  // Mock phone to 9 digits, can happen in Autosystem in production. We should validate it works in the iframe
  const clients =
    clientsData?.clients?.map((client) => ({
      ...client,
      phone: client.phone?.startsWith('56') ? client.phone.slice(2) : client.phone,
    })) || [];
  // Configuration state
  const [masterToken, setMasterToken] = useState('');
  const [showMasterToken, setShowMasterToken] = useState(false);
  const [userEmail, setUserEmail] = useState('fernando.smith@brunofritsch.cl');
  const [userName, setUserName] = useState('Feña');
  const [userRole, setUserRole] = useState<'seller' | 'admin'>('admin');
  const [userBrand, setUserBrand] = useState('TOYOTA');
  const [userBranchOffice, setUserBranchOffice] = useState('Maipú');

  // Authorization state
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [userJWT, setUserJWT] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null);
  const [userId, setUserId] = useState<number | null>(null);

  // Iframe state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchPhone, setSearchPhone] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // UI state
  const [showConfiguration, setShowConfiguration] = useState(true);
  const [searchPhoneInput, setSearchPhoneInput] = useState('');

  // Close lead state
  const [closeLeadDialogOpen, setCloseLeadDialogOpen] = useState(false);
  const [clientToClose, setClientToClose] = useState<Client | null>(null);
  const [closeStatus, setCloseStatus] = useState<
    'Éxito' | 'Fracaso' | 'Inactividad' | 'Sin interés' | 'No califica' | ''
  >('');
  const [isClosingLead, setIsClosingLead] = useState(false);
  const [closeLeadError, setCloseLeadError] = useState<string | null>(null);

  // Authorize user with master token
  const handleAuthorizeUser = async () => {
    if (!masterToken) {
      setAuthError('Por favor ingresa el Master Token');
      return;
    }

    if (!userEmail) {
      setAuthError('Por favor ingresa el email del usuario');
      return;
    }

    setIsAuthorizing(true);
    setAuthError(null);

    try {
      const response = await fetch(`${config.apiUrl}/brunoFritsch/authorize-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${masterToken}`,
        },
        body: JSON.stringify({
          email: userEmail,
          name: userName || undefined,
          role: userRole === 'admin' ? 'admin' : 'seller',
          brand: userBrand || undefined,
          branchOffice: userBranchOffice || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || 'Error al autorizar usuario');
      }

      const data = (await response.json()) as { token: string; type: string };
      setUserJWT(data.token);

      // Extract expiry date from JWT (exp claim)
      const decoded = jwtDecode<{ sub: number; exp: number }>(data.token);
      console.log('decoded', decoded);

      // exp is in seconds, convert to milliseconds
      const expiry = new Date(decoded.exp * 1000);
      setTokenExpiry(expiry);

      setUserId(decoded.sub);

      console.log('[BrunoFritschDemo] Usuario autorizado exitosamente');
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Error desconocido');
      setUserJWT(null);
      setTokenExpiry(null);
    } finally {
      setIsAuthorizing(false);
    }
  };

  // Send postMessage to iframe when client is selected
  useEffect(() => {
    if ((!selectedClient && !searchPhone) || !userJWT || !iframeRef.current) {
      return () => {};
    }
    // Wait for iframe to load
    const timer = setTimeout(() => {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            eventType: 'RELIF_USER_TOKEN',
            token: userJWT,
            name: userName,
            email: userEmail,
            role: userRole === 'admin' ? 'businessUnitAdmin' : 'seller',
            brand: userBrand,
            branchOffice: userBranchOffice,
          },
          window.location.origin
        );
        console.log('[BrunoFritschDemo] postMessage enviado al iframe');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedClient, searchPhone, userJWT, userName, userEmail, userRole, userBrand, userBranchOffice]);

  const handleSelectClient = (client: Client) => {
    if (!userJWT) {
      setAuthError('Por favor autoriza un usuario primero');
      return;
    }
    setSelectedClient(client);
    setSearchPhone(null);
  };

  const handleSearchByPhone = (phone: string) => {
    if (!userJWT) {
      setAuthError('Por favor autoriza un usuario primero');
      return;
    }
    setSearchPhone(phone);
    setSelectedClient(null);
  };

  const handleOpenCloseLeadDialog = (client: Client) => {
    if (!masterToken) {
      setCloseLeadError('Por favor ingresa el Master Token primero');
      return;
    }
    setClientToClose(client);
    setCloseStatus('');
    setCloseLeadError(null);
    setCloseLeadDialogOpen(true);
  };

  const handleCloseLead = async () => {
    if (!clientToClose || !masterToken) {
      return;
    }

    setIsClosingLead(true);
    setCloseLeadError(null);

    try {
      const response = await fetch(`${config.apiUrl}/brunoFritsch/close-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${masterToken}`,
        },
        body: JSON.stringify({
          phone: clientToClose.phone,
          status: closeStatus || null,
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || 'Error al cerrar el lead');
      }

      const data = (await response.json()) as { status: string; description: string };
      console.log('[BrunoFritschDemo] Lead cerrado exitosamente', data);

      // Close dialog and reset state
      setCloseLeadDialogOpen(false);
      setClientToClose(null);
      setCloseStatus('');

      // Optionally refresh clients list
      // You might want to invalidate the query here if using React Query
    } catch (error) {
      setCloseLeadError(error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setIsClosingLead(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5', overflow: 'hidden' }}>
      {/* Main Content - Left Side */}
      <Box sx={{ flex: selectedClient || searchPhone ? '0 0 65%' : '1 1 100%', overflow: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
            }}
          >
            🚗 Autosystem - Bruno Fritsch (DEMO)
          </Typography>
          <Chip label="Modo Demo" color="warning" />
        </Box>

        {/* Configuration Panel */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                mb: showConfiguration ? 2 : 0,
              }}
              onClick={() => setShowConfiguration(!showConfiguration)}
            >
              <Typography variant="h6">⚙️ Configuración de Demo</Typography>
              <IconButton>{showConfiguration ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
            </Box>

            {showConfiguration && (
              <>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Esta es una demo de integración, en producción este token no será necesario.
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {/* Master Token */}
                  <TextField
                    fullWidth
                    label="Master Token (BRUNO_FRITSCH_API_TOKEN)"
                    type={showMasterToken ? 'text' : 'password'}
                    value={masterToken}
                    onChange={(e) => setMasterToken(e.target.value)}
                    placeholder="Ingresa el token de API de Bruno Fritsch"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowMasterToken(!showMasterToken)} edge="end">
                              {showMasterToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Datos del Usuario a Autorizar
                  </Typography>

                  {/* User Configuration */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr 1fr' }, gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Email del Usuario"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="usuario@brunofritsch.cl"
                    />

                    <TextField
                      fullWidth
                      label="Nombre del Usuario"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Nombre completo"
                    />

                    <FormControl fullWidth>
                      <InputLabel>Rol</InputLabel>
                      <Select
                        value={userRole}
                        label="Rol"
                        onChange={(e) => setUserRole(e.target.value as 'seller' | 'admin')}
                      >
                        <MenuItem value="seller">Vendedor</MenuItem>
                        <MenuItem value="admin">Administrador</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Marca</InputLabel>
                      <Select value={userBrand} label="Marca" onChange={(e) => setUserBrand(e.target.value)}>
                        {BRANDS.map((brand) => (
                          <MenuItem key={brand} value={brand}>
                            {brand}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Sucursal</InputLabel>
                      <Select
                        value={userBranchOffice}
                        label="Sucursal"
                        onChange={(e) => setUserBranchOffice(e.target.value)}
                      >
                        {BRANCH_OFFICES.map((office) => (
                          <MenuItem key={office} value={office}>
                            {office}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Authorize Button */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleAuthorizeUser().catch(console.error);
                    }}
                    disabled={isAuthorizing || !masterToken || !userEmail}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {isAuthorizing ? 'Autorizando...' : 'Autorizar Usuario'}
                  </Button>

                  {/* Auth Error */}
                  {authError && (
                    <Alert severity="error" onClose={() => setAuthError(null)}>
                      {authError}
                    </Alert>
                  )}

                  {/* Success State */}
                  {userJWT && tokenExpiry && (
                    <Alert severity="success">
                      ✅ Usuario autorizado exitosamente
                      <br />
                      Token válido hasta: {tokenExpiry.toLocaleString('es-CL')}
                      <br />
                      ID de Usuario: {userId}
                      <br />
                      Token de Autenticación: {userJWT.substring(0, 50)}...
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Search by Phone */}
        {userJWT && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔍 Buscar Cliente por Teléfono
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={searchPhoneInput}
                  onChange={(e) => setSearchPhoneInput(e.target.value)}
                  placeholder="Ej: 56912345678"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && searchPhoneInput.trim()) {
                      handleSearchByPhone(searchPhoneInput.trim());
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (searchPhoneInput.trim()) {
                      handleSearchByPhone(searchPhoneInput.trim());
                    }
                  }}
                  disabled={!searchPhoneInput.trim()}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Buscar
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Clients Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📋 Hoja de Trabajo - {new Date().toLocaleDateString('es-CL')}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
              }}
            >
              Vendedor: {userName} ({userEmail})
            </Typography>

            {isLoadingClients && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    ml: 2,
                  }}
                >
                  Cargando clientes...
                </Typography>
              </Box>
            )}

            {!isLoadingClients && clients.length === 0 && (
              <Alert severity="info">No hay clientes disponibles para mostrar en la demo.</Alert>
            )}

            {!isLoadingClients && clients.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>
                        <strong>RUT</strong>
                      </TableCell>
                      <TableCell>
                        <strong>NOMBRE</strong>
                      </TableCell>
                      <TableCell>
                        <strong>VEHÍCULO</strong>
                      </TableCell>
                      <TableCell>
                        <strong>TELÉFONO</strong>
                      </TableCell>
                      <TableCell>
                        <strong>SUCURSAL</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>ACCIÓN</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow
                        key={client.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          bgcolor: selectedClient?.id === client.id ? '#e3f2fd' : 'inherit',
                        }}
                      >
                        <TableCell>{client.rut || '-'}</TableCell>
                        <TableCell>
                          {client.firstName} {client.lastName || ''}
                        </TableCell>
                        <TableCell>
                          {client.brandOfInterest}{' '}
                          {client.modelsOfInterest && client.modelsOfInterest.length > 0
                            ? client.modelsOfInterest[0]
                            : '-'}
                        </TableCell>
                        <TableCell>{client.phone}</TableCell>
                        <TableCell>{client.branchOffice || '-'}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleSelectClient(client)}
                              disabled={!userJWT}
                            >
                              🔍 Ver en Relif
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => handleOpenCloseLeadDialog(client)}
                              disabled={!masterToken}
                            >
                              ❌ Cerrar Lead
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
      {/* Iframe Container - Right Side */}
      {(selectedClient || searchPhone) && (
        <Box
          sx={{
            flex: '0 0 35%',
            borderLeft: '2px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
          }}
        >
          {/* Iframe Header */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f9f9f9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {selectedClient ? 'Cliente seleccionado' : 'Buscar Cliente'}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {selectedClient
                  ? `${selectedClient.firstName} ${selectedClient.lastName || ''}`
                  : `Teléfono: ${searchPhone}`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedClient(null);
                setSearchPhone(null);
              }}
            >
              Cerrar
            </Button>
          </Box>

          {/* Iframe */}
          <Box sx={{ flex: 1, overflow: 'hidden', border: '2px solid red' }}>
            <iframe
              ref={iframeRef}
              src={`/bruno-fritsch-client?phone=${selectedClient?.phone || searchPhone}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
              title={
                selectedClient
                  ? `Cliente ${selectedClient.firstName} ${selectedClient.lastName || ''}`
                  : `Buscar cliente ${searchPhone}`
              }
            />
          </Box>
        </Box>
      )}
      {/* Close Lead Dialog */}
      <Dialog open={closeLeadDialogOpen} onClose={() => setCloseLeadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cerrar Lead</DialogTitle>
        <DialogContent>
          {clientToClose && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Cliente: {clientToClose.firstName} {clientToClose.lastName || ''}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Teléfono: {clientToClose.phone}
              </Typography>
            </Box>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Estado de Cierre</InputLabel>
            <Select
              value={closeStatus}
              label="Estado de Cierre"
              onChange={(e) => setCloseStatus(e.target.value as typeof closeStatus)}
            >
              <MenuItem value="">Sin especificar</MenuItem>
              <MenuItem value="Éxito">Éxito</MenuItem>
              <MenuItem value="Fracaso">Fracaso</MenuItem>
              <MenuItem value="Inactividad">Inactividad</MenuItem>
              <MenuItem value="Sin interés">Sin interés</MenuItem>
              <MenuItem value="No califica">No califica</MenuItem>
            </Select>
          </FormControl>

          {closeLeadError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCloseLeadError(null)}>
              {closeLeadError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseLeadDialogOpen(false)} disabled={isClosingLead}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              handleCloseLead().catch((error) => {
                console.error('Error closing lead:', error);
              });
            }}
            variant="contained"
            color="error"
            disabled={isClosingLead || !masterToken}
          >
            {isClosingLead ? 'Cerrando...' : 'Cerrar Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrunoFritschDemo;
